import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Plus, Minus } from "lucide-react";
import { HoldingComparison } from "../hooks/useHoldingsComparisonController";

interface ComparisonSummaryProps {
  comparisonData: HoldingComparison[];
}

export const ComparisonSummary: React.FC<ComparisonSummaryProps> = ({ comparisonData }) => {
  // Calculate summary statistics
  const totalComparisons = comparisonData.length;
  const newHoldings = comparisonData.filter(item => item.status === 'new').length;
  const removedHoldings = comparisonData.filter(item => item.status === 'removed').length;
  const changedHoldings = comparisonData.filter(item => item.status === 'changed').length;
  const unchangedHoldings = comparisonData.filter(item => item.status === 'unchanged').length;

  // Calculate total value changes by currency
  const totalValueChangesByCurrency = comparisonData.reduce((currencyTotals, item) => {
    if (item.amountChange !== undefined && item.amountChange !== 0) {
      // Get currency from primary or secondary holding
      const currency = item.primaryHolding?.currency || 
                      item.secondaryHolding?.currency || 
                      "USD";
      
      if (!currencyTotals[currency]) {
        currencyTotals[currency] = 0;
      }
      currencyTotals[currency] += item.amountChange;
    }
    return currencyTotals;
  }, {} as Record<string, number>);

  const increasedValue = comparisonData.filter(item => (item.amountChange || 0) > 0).length;
  const decreasedValue = comparisonData.filter(item => (item.amountChange || 0) < 0).length;

  const formatCurrency = (amount: number, currency: string = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
      {/* Total Holdings */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground">Total Holdings</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="text-2xl font-bold">{totalComparisons}</div>
        </CardContent>
      </Card>

      {/* New Holdings */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground">New Holdings</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex items-center gap-2">
            <Plus className="h-4 w-4 text-green-600" />
            <span className="text-2xl font-bold text-green-600">{newHoldings}</span>
          </div>
        </CardContent>
      </Card>

      {/* Removed Holdings */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground">Removed Holdings</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex items-center gap-2">
            <Minus className="h-4 w-4 text-red-600" />
            <span className="text-2xl font-bold text-red-600">{removedHoldings}</span>
          </div>
        </CardContent>
      </Card>

      {/* Changed Holdings */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground">Changed Holdings</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-blue-600" />
            <span className="text-2xl font-bold text-blue-600">{changedHoldings}</span>
          </div>
        </CardContent>
      </Card>

      {/* Unchanged Holdings */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground">Unchanged</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="text-2xl font-bold text-muted-foreground">{unchangedHoldings}</div>
        </CardContent>
      </Card>

      {/* Net Value Changes by Currency */}
      {Object.entries(totalValueChangesByCurrency).map(([currency, totalChange]) => (
        <Card key={currency}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Net Change ({currency})</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex items-center gap-2">
              {totalChange >= 0 ? (
                <TrendingUp className="h-4 w-4 text-green-600" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-600" />
              )}
              <span className={`text-lg font-bold ${
                totalChange >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {formatCurrency(totalChange, currency)}
              </span>
            </div>
          </CardContent>
        </Card>
      ))}
      
      {/* Show a message if no value changes */}
      {Object.keys(totalValueChangesByCurrency).length === 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Net Value Change</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-lg font-bold text-muted-foreground">No Changes</div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}; 